/**
 * AssessmentSystem - Tracks learning progress and concept mastery
 * From PRD Section 8.1
 */

import Phaser from 'phaser';
import type {
  MasteryLevel,
  ConceptAssessment,
  ExplicitAssessment,
  LevelScore,
  StudentProfile,
  TeacherDashboardData,
  ClassProgress,
  StandardsReport,
} from '@/types';
import { MasteryLevel as ML } from '@/types';
import { CONCEPTS, getConceptsForLevel, NGSS_STANDARDS } from '@/data/concepts';
import type { ClassificationResultEvent } from './ClassificationSystem';

const STORAGE_KEY = 'cellsandlife_assessment';

export interface AssessmentConfig {
  /** Student identifier (optional, for multi-student support) */
  studentId?: string;
  /** Student name (optional) */
  studentName?: string;
}

export interface AssessmentState {
  conceptAssessments: Map<string, ConceptAssessment>;
  levelScores: Map<string, LevelScore>;
  totalTimeOnTask: number;
  sessionsCount: number;
  lastActive: Date;
}

/**
 * AssessmentSystem tracks implicit and explicit assessments
 * to calculate concept mastery levels
 */
export class AssessmentSystem {
  private scene: Phaser.Scene;
  private config: AssessmentConfig;
  private state: AssessmentState;
  private sessionStartTime: number;

  constructor(scene: Phaser.Scene, config: AssessmentConfig = {}) {
    this.scene = scene;
    this.config = {
      studentId: config.studentId ?? `student_${Date.now()}`,
      studentName: config.studentName,
    };

    this.sessionStartTime = Date.now();
    this.state = this.loadState();
    this.setupEventListeners();
  }

  /**
   * Set up event listeners for implicit assessment
   */
  private setupEventListeners(): void {
    // Classification attempts
    this.scene.events.on('classification:complete', this.handleClassificationComplete, this);

    // Evidence collection
    this.scene.events.on('evidence:collected', this.handleEvidenceCollected, this);

    // Level completion
    this.scene.events.on('level:complete', this.handleLevelComplete, this);

    // Hint requests
    this.scene.events.on('hint:requested', this.handleHintRequested, this);
  }

  /**
   * Handle classification attempt - implicit assessment
   */
  private handleClassificationComplete(event: ClassificationResultEvent): void {
    const { result } = event;

    // Get concepts being tested in current level
    const levelId = this.getCurrentLevelId();
    const concepts = getConceptsForLevel(levelId);

    for (const concept of concepts) {
      const assessment = this.getOrCreateConceptAssessment(concept.id);

      if (result.correct) {
        assessment.implicitIndicators.correctClassifications++;
      } else {
        assessment.implicitIndicators.incorrectClassifications++;
      }

      // Recalculate mastery
      assessment.currentMastery = this.calculateMastery(assessment);

      this.state.conceptAssessments.set(concept.id, assessment);
    }

    this.saveState();
    this.emitMasteryUpdate();
  }

  /**
   * Handle evidence collection
   */
  private handleEvidenceCollected(): void {
    const levelId = this.getCurrentLevelId();
    const concepts = getConceptsForLevel(levelId);

    for (const concept of concepts) {
      const assessment = this.getOrCreateConceptAssessment(concept.id);
      assessment.implicitIndicators.evidenceGathered++;
      this.state.conceptAssessments.set(concept.id, assessment);
    }

    this.saveState();
  }

  /**
   * Handle level completion
   */
  private handleLevelComplete(event: { levelId: string; score: LevelScore }): void {
    this.state.levelScores.set(event.levelId, event.score);

    // Mark concepts as assessed
    const concepts = getConceptsForLevel(event.levelId);
    for (const concept of concepts) {
      const assessment = this.getOrCreateConceptAssessment(concept.id);
      assessment.implicitIndicators.simulationsCompleted++;
      assessment.currentMastery = this.calculateMastery(assessment);
      this.state.conceptAssessments.set(concept.id, assessment);
    }

    this.saveState();
    this.emitMasteryUpdate();
  }

  /**
   * Handle hint request
   */
  private handleHintRequested(): void {
    const levelId = this.getCurrentLevelId();
    const concepts = getConceptsForLevel(levelId);

    for (const concept of concepts) {
      const assessment = this.getOrCreateConceptAssessment(concept.id);
      assessment.implicitIndicators.hintsRequested++;
      this.state.conceptAssessments.set(concept.id, assessment);
    }

    this.saveState();
  }

  /**
   * Get or create a concept assessment
   */
  private getOrCreateConceptAssessment(conceptId: string): ConceptAssessment {
    const existing = this.state.conceptAssessments.get(conceptId);
    if (existing) return existing;

    const concept = CONCEPTS.find((c) => c.id === conceptId);
    if (!concept) {
      throw new Error(`Unknown concept: ${conceptId}`);
    }

    const newAssessment: ConceptAssessment = {
      conceptId,
      conceptText: concept.text,
      weight: concept.weight,
      assessmentType: 'implicit',
      implicitIndicators: {
        correctClassifications: 0,
        incorrectClassifications: 0,
        evidenceGathered: 0,
        simulationsCompleted: 0,
        hintsRequested: 0,
        timeOnTask: 0,
      },
      explicitAssessments: [],
      currentMastery: ML.NOT_INTRODUCED,
    };

    return newAssessment;
  }

  /**
   * Calculate mastery level based on indicators
   */
  private calculateMastery(assessment: ConceptAssessment): MasteryLevel {
    const { implicitIndicators: ind, explicitAssessments } = assessment;

    const totalAttempts = ind.correctClassifications + ind.incorrectClassifications;
    const accuracy = totalAttempts > 0 ? ind.correctClassifications / totalAttempts : 0;

    // Explicit assessments override implicit
    if (explicitAssessments.length > 0) {
      const explicitAccuracy =
        explicitAssessments.filter((e) => e.correct).length / explicitAssessments.length;

      if (explicitAccuracy >= 0.95) return ML.MASTERED;
      if (explicitAccuracy >= 0.8) return ML.PROFICIENT;
      if (explicitAccuracy >= 0.6) return ML.DEVELOPING;
      if (explicitAccuracy >= 0.4) return ML.PRACTICING;
      return ML.INTRODUCED;
    }

    // Implicit assessment based mastery
    if (totalAttempts === 0) {
      return ind.evidenceGathered > 0 ? ML.INTRODUCED : ML.NOT_INTRODUCED;
    }

    // Account for hints (too many hints reduces mastery)
    const hintPenalty = Math.min(0.2, ind.hintsRequested * 0.05);
    const adjustedAccuracy = Math.max(0, accuracy - hintPenalty);

    if (adjustedAccuracy >= 0.9 && totalAttempts >= 5) return ML.MASTERED;
    if (adjustedAccuracy >= 0.8 && totalAttempts >= 3) return ML.PROFICIENT;
    if (adjustedAccuracy >= 0.6 && totalAttempts >= 2) return ML.DEVELOPING;
    if (adjustedAccuracy >= 0.4) return ML.PRACTICING;
    if (totalAttempts > 0) return ML.INTRODUCED;

    return ML.NOT_INTRODUCED;
  }

  /**
   * Track explicit assessment (quiz, reflection)
   */
  public trackExplicitAssessment(
    conceptId: string,
    questionId: string,
    response: string,
    correct: boolean
  ): void {
    const assessment = this.getOrCreateConceptAssessment(conceptId);

    const explicit: ExplicitAssessment = {
      questionId,
      response,
      correct,
      timestamp: new Date(),
    };

    assessment.explicitAssessments.push(explicit);
    assessment.assessmentType = 'explicit';
    assessment.currentMastery = this.calculateMastery(assessment);

    this.state.conceptAssessments.set(conceptId, assessment);
    this.saveState();
    this.emitMasteryUpdate();
  }

  /**
   * Get mastery level for a concept
   */
  public getConceptMastery(conceptId: string): MasteryLevel {
    const assessment = this.state.conceptAssessments.get(conceptId);
    return assessment?.currentMastery ?? ML.NOT_INTRODUCED;
  }

  /**
   * Get all concept assessments
   */
  public getAllAssessments(): ConceptAssessment[] {
    return Array.from(this.state.conceptAssessments.values());
  }

  /**
   * Get overall progress (percentage of concepts at DEVELOPING or higher)
   */
  public getOverallProgress(): number {
    const totalConcepts = CONCEPTS.length;
    const developingOrHigher = Array.from(this.state.conceptAssessments.values()).filter(
      (a) => a.currentMastery >= ML.DEVELOPING
    ).length;

    return developingOrHigher / totalConcepts;
  }

  /**
   * Get struggling areas (concepts below DEVELOPING)
   */
  public getStrugglingAreas(): string[] {
    return Array.from(this.state.conceptAssessments.entries())
      .filter(([_, a]) => a.currentMastery < ML.DEVELOPING && a.currentMastery > ML.NOT_INTRODUCED)
      .map(([id]) => id);
  }

  /**
   * Get strengths (concepts at PROFICIENT or MASTERED)
   */
  public getStrengths(): string[] {
    return Array.from(this.state.conceptAssessments.entries())
      .filter(([_, a]) => a.currentMastery >= ML.PROFICIENT)
      .map(([id]) => id);
  }

  /**
   * Generate student profile for export
   */
  public generateStudentProfile(): StudentProfile {
    const completedLevels = Array.from(this.state.levelScores.keys());
    const conceptMastery = new Map<string, MasteryLevel>();

    for (const [id, assessment] of this.state.conceptAssessments) {
      conceptMastery.set(id, assessment.currentMastery);
    }

    // Update time on task
    const sessionTime = (Date.now() - this.sessionStartTime) / 1000;
    this.state.totalTimeOnTask += sessionTime;
    this.sessionStartTime = Date.now();

    return {
      id: this.config.studentId!,
      name: this.config.studentName,
      completedLevels,
      conceptMastery,
      timeOnTask: this.state.totalTimeOnTask,
      strugglingAreas: this.getStrugglingAreas(),
      strengths: this.getStrengths(),
      lastActive: new Date(),
    };
  }

  /**
   * Generate teacher dashboard data (single student)
   */
  public generateDashboardData(): TeacherDashboardData {
    const profile = this.generateStudentProfile();

    // Class progress (single student = their progress)
    const classProgress: ClassProgress = {
      averageCompletion: this.getOverallProgress(),
      conceptMasteryDistribution: new Map(
        CONCEPTS.map((c) => [c.id, [this.getConceptMastery(c.id)]])
      ),
      commonMisconceptions: [], // Would need multiple students to determine
    };

    // Standards report
    const standardsReport: StandardsReport[] = NGSS_STANDARDS.map((standard) => {
      const conceptScores = standard.concepts.map((cId) => {
        const mastery = this.getConceptMastery(cId);
        return mastery / ML.MASTERED; // Normalize to 0-1
      });

      const average = conceptScores.length > 0
        ? conceptScores.reduce((a, b) => a + b, 0) / conceptScores.length
        : 0;

      return {
        standardId: standard.id,
        classAverage: average,
        evidenceItems: standard.concepts.filter(
          (cId) => this.getConceptMastery(cId) >= ML.DEVELOPING
        ),
      };
    });

    return {
      classProgress,
      studentProfiles: [profile],
      standardsReport,
    };
  }

  /**
   * Export data as JSON
   */
  public exportToJSON(): string {
    const data = this.generateDashboardData();

    // Convert Maps to objects for JSON serialization
    const serializable = {
      ...data,
      classProgress: {
        ...data.classProgress,
        conceptMasteryDistribution: Object.fromEntries(
          data.classProgress.conceptMasteryDistribution
        ),
      },
      studentProfiles: data.studentProfiles.map((p) => ({
        ...p,
        conceptMastery: Object.fromEntries(p.conceptMastery),
        lastActive: p.lastActive.toISOString(),
      })),
      exportDate: new Date().toISOString(),
      gameVersion: '1.0.0',
    };

    return JSON.stringify(serializable, null, 2);
  }

  /**
   * Export data as CSV
   */
  public exportToCSV(): string {
    const assessments = this.getAllAssessments();

    const headers = [
      'Concept ID',
      'Concept Text',
      'Weight',
      'Mastery Level',
      'Correct Classifications',
      'Incorrect Classifications',
      'Evidence Gathered',
      'Hints Requested',
    ];

    const rows = assessments.map((a) => [
      a.conceptId,
      `"${a.conceptText}"`,
      a.weight,
      ML[a.currentMastery],
      a.implicitIndicators.correctClassifications,
      a.implicitIndicators.incorrectClassifications,
      a.implicitIndicators.evidenceGathered,
      a.implicitIndicators.hintsRequested,
    ]);

    return [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  }

  /**
   * Download data as file
   */
  public downloadExport(format: 'json' | 'csv'): void {
    const data = format === 'json' ? this.exportToJSON() : this.exportToCSV();
    const mimeType = format === 'json' ? 'application/json' : 'text/csv';
    const filename = `cells-and-life-progress-${this.config.studentId}.${format}`;

    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  }

  /**
   * Get current level ID from scene data or registry
   */
  private getCurrentLevelId(): string {
    return (
      this.scene.registry.get('currentLevelId') ||
      this.scene.data.get('levelId') ||
      'unknown'
    );
  }

  /**
   * Emit mastery update event
   */
  private emitMasteryUpdate(): void {
    this.scene.events.emit('assessment:mastery:updated', {
      overallProgress: this.getOverallProgress(),
      assessments: this.getAllAssessments(),
    });
  }

  /**
   * Load state from localStorage
   */
  private loadState(): AssessmentState {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          conceptAssessments: new Map(Object.entries(parsed.conceptAssessments || {})),
          levelScores: new Map(Object.entries(parsed.levelScores || {})),
          totalTimeOnTask: parsed.totalTimeOnTask || 0,
          sessionsCount: (parsed.sessionsCount || 0) + 1,
          lastActive: new Date(parsed.lastActive || Date.now()),
        };
      }
    } catch (e) {
      console.warn('Failed to load assessment state:', e);
    }

    return {
      conceptAssessments: new Map(),
      levelScores: new Map(),
      totalTimeOnTask: 0,
      sessionsCount: 1,
      lastActive: new Date(),
    };
  }

  /**
   * Save state to localStorage
   */
  private saveState(): void {
    try {
      const serializable = {
        conceptAssessments: Object.fromEntries(this.state.conceptAssessments),
        levelScores: Object.fromEntries(this.state.levelScores),
        totalTimeOnTask: this.state.totalTimeOnTask,
        sessionsCount: this.state.sessionsCount,
        lastActive: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
    } catch (e) {
      console.warn('Failed to save assessment state:', e);
    }
  }

  /**
   * Reset all assessment data
   */
  public reset(): void {
    this.state = {
      conceptAssessments: new Map(),
      levelScores: new Map(),
      totalTimeOnTask: 0,
      sessionsCount: 1,
      lastActive: new Date(),
    };
    localStorage.removeItem(STORAGE_KEY);
    this.emitMasteryUpdate();
  }

  /**
   * Clean up
   */
  public destroy(): void {
    // Save final time
    const sessionTime = (Date.now() - this.sessionStartTime) / 1000;
    this.state.totalTimeOnTask += sessionTime;
    this.saveState();

    this.scene.events.off('classification:complete', this.handleClassificationComplete, this);
    this.scene.events.off('evidence:collected', this.handleEvidenceCollected, this);
    this.scene.events.off('level:complete', this.handleLevelComplete, this);
    this.scene.events.off('hint:requested', this.handleHintRequested, this);
  }
}
